// Need to use the React-specific entry point to import createApi
import { createApi } from "@reduxjs/toolkit/query/react";
import { baseRestQueryResult as baseQuery, showNotification } from "@/services/api";
import { UnitCode, UnitCodeForm } from "@/types/unitcode.type";
import { IDropdown } from "@/constants/default";
import { ListResponse } from "@/types/response.type";

// Define a service using a base URL and expected endpoints
export const unitCodeApi = createApi({
    reducerPath: "unitCodeApi",
    baseQuery,
    tagTypes: ['UnitCode'],
    endpoints: (builder) => 
    ({
        getUnitCodes: builder.query<ListResponse<UnitCode>, {}>({
            query: (data) => ({ url: `unitcode`, params: data }),
            keepUnusedDataFor: 0,
            transformResponse: (rawResult: { data: ListResponse<UnitCode> }, meta) => {
                return rawResult.data
            },
        }),
        getDropDownUnitCodes: builder.query<IDropdown[], number | void>({
            query: (data) => ({ url: `unitcode/dropdown`, params: {status: data ? data : ""} }),
            keepUnusedDataFor: 0,
            transformResponse: (rawResult: { data: IDropdown[] }, meta) => {
              return rawResult.data
            },
        }),
        getUnitCode: builder.query<UnitCode, string>({
            query: (id) => `unitcode/${id}`,
            keepUnusedDataFor: 0,
            transformResponse: (rawResult: { data: UnitCode }, meta) => {
                return rawResult.data
            }
        }),
        addUnitCode: builder.mutation<UnitCode, UnitCodeForm>({
            query: (body) => ({
                url: 'unitcode',
                method: 'POST',
                body,
            }),
            async onQueryStarted(body, { queryFulfilled }) {
                try {
                  const { data, meta } = await queryFulfilled
                  // `onSuccess` side-effect
                  showNotification("POST", "success")
                } catch (err: any) {
                  
                  // `onError` side-effect
                  showNotification("POST", "error", err?.error?.data?.message || "error")
                }
            }
        }),
        editUnitCode: builder.mutation<UnitCode, Pick<UnitCode, "id">>({
            query: ({ id, ...body }) => ({
                url: `unitcode/${id}`,
                method: 'PUT',
                body,
            }),
            async onQueryStarted(body, { queryFulfilled }) {
                try {
                  const { data, meta } = await queryFulfilled
                  // `onSuccess` side-effect
                  showNotification("PUT", "success")
                } catch (err: any) {
                  
                  // `onError` side-effect
                  showNotification("PUT", "error", err?.error?.data?.message || "error")
                }
            }
        }),
        deleteUnitCode: builder.mutation<string, string>({
            query: (id) => ({
                url: `unitcode/${id}`,
                method: 'DELETE',
            }),
            async onQueryStarted(body, { queryFulfilled }) {
                try {
                  const { data, meta } = await queryFulfilled
                  // `onSuccess` side-effect
                  showNotification("DELETE", "success")
                } catch (err: any) {
                  
                  // `onError` side-effect
                  showNotification("DELETE", "error", err?.error?.data?.message || "error")
                }
            }
        }),
    }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const { useGetUnitCodesQuery, useGetDropDownUnitCodesQuery, useGetUnitCodeQuery, useAddUnitCodeMutation, useEditUnitCodeMutation, useDeleteUnitCodeMutation } = unitCodeApi;
